import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { listFirmExpenses, createFirmExpense, approveFirmExpense, payFirmExpense, rejectFirmExpense, cancelRecurringExpense, getFirmExpense, listSuppliers, listGLAccounts } from '../api';
import { formatCurrency } from '../utils/currency';
import { useConfirm } from './ConfirmProvider';

// Matches legal_ops.models.FirmExpense.Category exactly — previously listed
// POSTAGE/STATIONERY/TRANSPORT, none of which are valid backend choices
// (submitting one 400'd), while omitting several real ones.
const CATEGORY_OPTIONS = [
  { value: 'RENT', label: 'Rent' },
  { value: 'UTILITIES', label: 'Utilities (Water, Electricity)' },
  { value: 'TELEPHONE', label: 'Telephone' },
  { value: 'INTERNET', label: 'Internet' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'SALARIES', label: 'Salaries & Wages' },
  { value: 'PROFESSIONAL_FEES', label: 'Professional Fees' },
  { value: 'SUBSCRIPTIONS', label: 'Subscriptions & Licenses' },
  { value: 'MAINTENANCE', label: 'Maintenance & Repairs' },
  { value: 'CLEANING', label: 'Cleaning Services' },
  { value: 'SECURITY', label: 'Security' },
  { value: 'MARKETING', label: 'Marketing & Advertising' },
  { value: 'TRAINING', label: 'Training & Development' },
  { value: 'LICENSES', label: 'Licenses & Permits' },
  { value: 'BANK_CHARGES', label: 'Bank Charges' },
  { value: 'REFRESHMENTS', label: 'Refreshments (Coffee, Water, etc.)' },
  { value: 'OFFICE_SUPPLIES', label: 'Office Supplies' },
  { value: 'OTHER', label: 'Other' },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: 'BANK_TRANSFER', label: 'Bank Transfer / EFT' },
  { value: 'DEBIT_ORDER', label: 'Debit Order' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CARD', label: 'Card' },
];

const STATUS_COLORS = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-blue-100 text-blue-700',
  PAID: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-600',
  REVERSED: 'bg-gray-300 text-gray-700',
};

const EMPTY_FORM = {
  description: '', category: 'OTHER', amount: '', date: new Date().toISOString().slice(0, 10),
  supplier: '', gl_account: '', reference: '', is_recurring: false, recurrence_frequency: 'MONTHLY', notes: '',
};

function FirmExpenseManagement({ authAxios, showFormMessage, userRole }) {
  const confirm = useConfirm();
  const [expenses, setExpenses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [payingId, setPayingId] = useState(null);
  const [payMethod, setPayMethod] = useState('BANK_TRANSFER');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [viewingExpense, setViewingExpense] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [isSubmittingPay, setIsSubmittingPay] = useState(false);
  const isSubmittingFormRef = useRef(false);
  const isSubmittingPayRef = useRef(false);

  const canManage = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);
  // Matches the backend's IsFinanceOperator (approve/pay/reject/cancel-recurring).
  const canApprove = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchExpenses = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      const data = await listFirmExpenses(authAxios, params);
      setExpenses(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to load expenses.'); }
    finally { setIsLoading(false); }
  }, [authAxios, showFormMessage, statusFilter, categoryFilter]);

  const fetchRef = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([listSuppliers(authAxios, { is_active: true }), listGLAccounts(authAxios, { is_active: true, category: 'EXPENSE' })]);
      setSuppliers(Array.isArray(s) ? s : []);
      setAccounts(Array.isArray(a) ? a : []);
    } catch {
      showFormMessage('error', 'Failed to load suppliers or GL accounts.');
    }
  }, [authAxios, showFormMessage]);

  useEffect(() => { fetchExpenses(); fetchRef(); }, [fetchExpenses, fetchRef]);

  // Auto-open an expense's detail panel when deep-linked from Accounting Review.
  useEffect(() => {
    const handler = async (e) => {
      const detail = e.detail;
      if (!detail || detail.tab !== 'firmExpenses' || detail.type !== 'firm_expense') return;
      try {
        const expense = await getFirmExpense(authAxios, detail.id);
        setViewingExpense(expense);
      } catch { showFormMessage('error', 'Failed to load the requested expense.'); }
    };
    window.addEventListener('open-resource-detail', handler);
    return () => window.removeEventListener('open-resource-detail', handler);
  }, [authAxios, showFormMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmittingFormRef.current) return;
    isSubmittingFormRef.current = true;
    setIsSubmittingForm(true);
    try {
      const payload = { ...formData };
      if (!payload.supplier) delete payload.supplier;
      if (!payload.gl_account) delete payload.gl_account;
      if (!payload.is_recurring) delete payload.recurrence_frequency;
      await createFirmExpense(authAxios, payload);
      showFormMessage('success', 'Expense submitted.');
      setShowForm(false);
      setFormData(EMPTY_FORM);
      fetchExpenses();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to submit expense.');
    } finally {
      isSubmittingFormRef.current = false;
      setIsSubmittingForm(false);
    }
  };

  const refreshViewingExpense = async (id) => {
    try {
      const expense = await getFirmExpense(authAxios, id);
      setViewingExpense(v => (v && v.id === id ? expense : v));
    } catch { /* detail panel just shows stale data until reopened */ }
  };

  const handleApprove = async (id) => {
    const ok = await confirm({ title: 'Approve Expense', message: 'Approve this expense and post it to the GL?', confirmText: 'Approve' });
    if (!ok) return;
    setBusyId(id);
    try {
      await approveFirmExpense(authAxios, id);
      showFormMessage('success', 'Expense approved.');
      fetchExpenses();
      if (viewingExpense && viewingExpense.id === id) refreshViewingExpense(id);
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed to approve.'); }
    finally { setBusyId(null); }
  };

  const handleReject = async (id) => {
    const ok = await confirm({ title: 'Reject Expense', message: 'Reject this pending expense?', confirmText: 'Reject', variant: 'danger' });
    if (!ok) return;
    setBusyId(id);
    try {
      await rejectFirmExpense(authAxios, id);
      showFormMessage('success', 'Expense rejected.');
      fetchExpenses();
      if (viewingExpense && viewingExpense.id === id) refreshViewingExpense(id);
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed to reject.'); }
    finally { setBusyId(null); }
  };

  const handlePay = async (id) => {
    if (isSubmittingPayRef.current) return;
    const ok = await confirm({ title: 'Record Payment', message: 'Mark this expense as paid?', confirmText: 'Mark Paid' });
    if (!ok) return;
    isSubmittingPayRef.current = true;
    setIsSubmittingPay(true);
    try {
      await payFirmExpense(authAxios, id, { payment_method: payMethod });
      showFormMessage('success', 'Expense paid.');
      setPayingId(null);
      fetchExpenses();
      if (viewingExpense && viewingExpense.id === id) refreshViewingExpense(id);
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to record payment.');
    } finally {
      isSubmittingPayRef.current = false;
      setIsSubmittingPay(false);
    }
  };

  const handleCancelRecurring = async (id) => {
    const ok = await confirm({
      title: 'Cancel Recurring Expense',
      message: 'Cancel future auto-generation for this recurring expense?',
      confirmText: 'Cancel Recurring',
      variant: 'warning',
    });
    if (!ok) return;
    setBusyId(id);
    try {
      await cancelRecurringExpense(authAxios, id);
      showFormMessage('success', 'Recurring schedule cancelled.');
      fetchExpenses();
      if (viewingExpense && viewingExpense.id === id) refreshViewingExpense(id);
    } catch (err) { showFormMessage('error', err.response?.data?.detail || 'Failed to cancel recurring.'); }
    finally { setBusyId(null); }
  };

  const filtered = useMemo(() => {
    if (!searchTerm) return expenses;
    const term = searchTerm.toLowerCase();
    return expenses.filter(e => (e.description || '').toLowerCase().includes(term) || (e.reference || '').toLowerCase().includes(term));
  }, [expenses, searchTerm]);

  // Summary totals
  const totals = useMemo(() => {
    const pending = expenses.filter(e => e.status === 'PENDING').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
    const approved = expenses.filter(e => e.status === 'APPROVED').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
    const paid = expenses.filter(e => e.status === 'PAID').reduce((s, e) => s + parseFloat(e.amount || 0), 0);
    return { pending, approved, paid };
  }, [expenses]);

  const updateField = (field, value) => setFormData(f => ({ ...f, [field]: value }));
  const openDetailPanel = (expense) => setViewingExpense(expense);
  const closeDetailPanel = () => setViewingExpense(null);

  return (
    <div className="animate__animated animate__fadeIn">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Firm Expenses</h2>
          <p className="text-gray-500 text-sm mt-1">Track rent, utilities, subscriptions, and general expenses</p>
        </div>
        {canManage && (
          <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium">+ New Expense</button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase">Pending Approval</p>
          <p className="text-lg font-bold text-yellow-600">{formatCurrency(totals.pending)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase">Approved (Unpaid)</p>
          <p className="text-lg font-bold text-blue-600">{formatCurrency(totals.approved)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <p className="text-xs text-gray-500 uppercase">Paid This Period</p>
          <p className="text-lg font-bold text-green-600">{formatCurrency(totals.paid)}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input type="text" placeholder="Search expenses..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Statuses</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="PAID">Paid</option>
          <option value="REJECTED">Rejected</option>
          <option value="REVERSED">Reversed</option>
        </select>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Categories</option>
          {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Expense List */}
      {isLoading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm"><p className="text-gray-500">No expenses found.</p></div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Recurring</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(e => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-700">{e.date}</td>
                  <td className="px-4 py-3 text-gray-800">
                    <button onClick={() => openDetailPanel(e)} aria-label={`View details for expense: ${e.description}`} className="hover:underline text-left">{e.description}</button>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{e.category_display || CATEGORY_OPTIONS.find(c => c.value === e.category)?.label || e.category}</td>
                  <td className="px-4 py-3 text-gray-600">{e.supplier_name || '—'}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatCurrency(e.amount || 0)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[e.status] || 'bg-gray-100 text-gray-600'}`}>
                      {e.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {e.is_recurring ? (
                      <span className="inline-flex items-center gap-1">
                        <span className="text-blue-600 font-medium text-xs">{e.recurrence_frequency}</span>
                        {e.recurring_is_cancelled && <span className="text-red-500 text-[10px] font-bold">(Cancelled)</span>}
                      </span>
                    ) : e.is_auto_generated ? (
                      <span className="text-xs text-gray-400 italic">Auto</span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button onClick={() => openDetailPanel(e)} aria-label={`View expense details: ${e.description}`} className="text-gray-600 hover:text-gray-900 text-xs font-medium">View</button>
                    {canManage && e.status === 'PENDING' && canApprove && (
                      <>
                        <button disabled={busyId === e.id} onClick={() => handleApprove(e.id)} aria-label={`Approve expense: ${e.description}`} className="text-green-600 hover:text-green-800 text-xs font-medium disabled:opacity-50">Approve</button>
                        <button disabled={busyId === e.id} onClick={() => handleReject(e.id)} aria-label={`Reject expense: ${e.description}`} className="text-red-600 hover:text-red-800 text-xs font-medium disabled:opacity-50">Reject</button>
                      </>
                    )}
                    {canManage && e.status === 'APPROVED' && (
                      <button onClick={() => { setPayingId(e.id); setPayMethod('BANK_TRANSFER'); }} aria-label={`Record payment for expense: ${e.description}`} className="text-blue-600 hover:text-blue-800 text-xs font-medium">Pay</button>
                    )}
                    {canManage && e.is_recurring && !e.recurring_is_cancelled && (
                      <button disabled={busyId === e.id} onClick={() => handleCancelRecurring(e.id)} aria-label={`Cancel recurring schedule for expense: ${e.description}`} className="text-orange-600 hover:text-orange-800 text-xs font-medium disabled:opacity-50">Cancel Recurring</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New Expense Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">New Firm Expense</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <input type="text" required value={formData.description} onChange={e => updateField('description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. BPC electricity bill – January" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                  <select value={formData.category} onChange={e => updateField('category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (BWP) *</label>
                  <input type="number" step="0.01" required value={formData.amount} onChange={e => updateField('amount', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input type="date" required value={formData.date} onChange={e => updateField('date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier</label>
                  <select value={formData.supplier} onChange={e => updateField('supplier', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">None</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GL Account</label>
                  <select value={formData.gl_account} onChange={e => updateField('gl_account', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">Auto-assign by category</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                <input type="text" value={formData.reference} onChange={e => updateField('reference', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Invoice/receipt number" />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={formData.is_recurring} onChange={e => updateField('is_recurring', e.target.checked)} />
                Recurring Expense
              </label>
              {formData.is_recurring && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recurrence</label>
                  <select value={formData.recurrence_frequency} onChange={e => updateField('recurrence_frequency', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="WEEKLY">Weekly</option>
                    <option value="MONTHLY">Monthly</option>
                    <option value="QUARTERLY">Quarterly</option>
                    <option value="ANNUALLY">Annually</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea value={formData.notes} onChange={e => updateField('notes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={2} />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button type="submit" disabled={isSubmittingForm} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  {isSubmittingForm ? 'Submitting…' : 'Submit Expense'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pay Modal */}
      {payingId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Record Payment</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select value={payMethod} onChange={e => setPayMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  {PAYMENT_METHOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setPayingId(null)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button onClick={() => handlePay(payingId)} disabled={isSubmittingPay} className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  {isSubmittingPay ? 'Recording…' : 'Mark Paid'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Expense Detail Panel */}
      {viewingExpense && (
        <div className="fixed inset-0 overflow-hidden z-50">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetailPanel}></div>
            <section className="absolute inset-y-0 right-0 pl-10 max-w-full flex">
              <div className="w-screen max-w-md">
                <div className="h-full flex flex-col bg-white shadow-xl">
                  <div className="px-4 py-6 sm:px-6 bg-white border-b">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">{viewingExpense.description}</h2>
                        <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${STATUS_COLORS[viewingExpense.status] || 'bg-gray-50 text-gray-700 ring-gray-200'}`}>
                            {viewingExpense.status}
                          </span>
                          <span>{viewingExpense.category_display || viewingExpense.category}</span>
                        </div>
                      </div>
                      <div className="ml-3 h-7 flex items-center">
                        <button onClick={closeDetailPanel} aria-label="Close expense details" className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500">×</button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-6">
                    <div className="grid grid-cols-1 gap-3 mb-4 rounded-lg border border-gray-200 bg-white p-4">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="rounded-md bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
                          <p className="text-xs text-gray-600">Amount</p>
                          <p className="font-semibold text-gray-900">{formatCurrency(viewingExpense.amount || 0)}</p>
                        </div>
                        <div className="rounded-md bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
                          <p className="text-xs text-gray-600">VAT</p>
                          <p className="font-semibold text-gray-900">{formatCurrency(viewingExpense.vat_amount || 0)}</p>
                        </div>
                        <div className="rounded-md bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
                          <p className="text-xs text-gray-600">Date</p>
                          <p className="font-semibold text-gray-900">{viewingExpense.date}</p>
                        </div>
                        <div className="rounded-md bg-gray-50 px-3 py-2 ring-1 ring-inset ring-gray-200">
                          <p className="text-xs text-gray-600">GL Account</p>
                          <p className="font-semibold text-gray-900">{viewingExpense.gl_expense_account_name || '—'}</p>
                        </div>
                      </div>
                      {viewingExpense.supplier_name && (
                        <div className="text-sm">
                          <p className="text-gray-600">Supplier</p>
                          <p className="font-semibold text-gray-900">{viewingExpense.supplier_name}</p>
                        </div>
                      )}
                      {viewingExpense.reference && (
                        <div className="text-sm">
                          <p className="text-gray-600">Reference</p>
                          <p className="font-semibold text-gray-900">{viewingExpense.reference}</p>
                        </div>
                      )}
                      {viewingExpense.notes && (
                        <div className="text-sm">
                          <p className="text-gray-600">Notes</p>
                          <p className="text-gray-900">{viewingExpense.notes}</p>
                        </div>
                      )}
                      {viewingExpense.status === 'PAID' && (
                        <div className="rounded-md bg-emerald-50 px-3 py-2 ring-1 ring-inset ring-emerald-200 text-sm">
                          <p className="text-xs text-emerald-800">Payment</p>
                          <p className="font-semibold text-emerald-900">{viewingExpense.payment_method_display} on {viewingExpense.payment_date}</p>
                        </div>
                      )}
                      {viewingExpense.approved_by_username && (
                        <div className="text-sm">
                          <p className="text-gray-600">Approved By</p>
                          <p className="font-semibold text-gray-900">{viewingExpense.approved_by_username}</p>
                        </div>
                      )}
                      {viewingExpense.gl_entry_number && (
                        <div className="text-sm">
                          <p className="text-gray-600">GL Journal Entry</p>
                          <p className="font-semibold text-gray-900">{viewingExpense.gl_entry_number}</p>
                        </div>
                      )}
                      {viewingExpense.is_recurring && (
                        <div className="rounded-md bg-blue-50 px-3 py-2 ring-1 ring-inset ring-blue-200 text-sm">
                          <p className="text-xs text-blue-800">Recurring</p>
                          <p className="font-semibold text-blue-900">
                            {viewingExpense.recurrence_frequency}
                            {viewingExpense.recurring_is_cancelled ? ' (Cancelled)' : ''}
                          </p>
                        </div>
                      )}
                      {viewingExpense.is_auto_generated && viewingExpense.parent_recurring_expense_description && (
                        <div className="text-sm">
                          <p className="text-gray-600">Auto-Generated From</p>
                          <p className="font-semibold text-gray-900">{viewingExpense.parent_recurring_expense_description}</p>
                        </div>
                      )}
                      {canManage && (
                        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                          {viewingExpense.status === 'PENDING' && canApprove && (
                            <>
                              <button onClick={() => handleApprove(viewingExpense.id)} className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 text-xs font-medium">Approve</button>
                              <button onClick={() => handleReject(viewingExpense.id)} className="px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 text-xs font-medium">Reject</button>
                            </>
                          )}
                          {viewingExpense.status === 'APPROVED' && (
                            <button onClick={() => { setPayingId(viewingExpense.id); setPayMethod('BANK_TRANSFER'); }} className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-xs font-medium">Record Payment</button>
                          )}
                          {viewingExpense.is_recurring && !viewingExpense.recurring_is_cancelled && (
                            <button onClick={() => handleCancelRecurring(viewingExpense.id)} className="px-3 py-1.5 bg-orange-600 text-white rounded-md hover:bg-orange-700 text-xs font-medium">Cancel Recurring</button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}

export default FirmExpenseManagement;
